import { DeepMap } from 'react-hook-form';
import { JobsiteUpdateInput } from 'apollo/generated/client-operations';
import { ensureNonUndefinedFields, titleToKebabCase } from 'utils';
import { FormInputTypes, TypedFormInputs } from 'components/form';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import { Jobsite } from './types';
import {
  documentsSectionInputs,
  getDocumentsSectionDefaultValues,
  getOnboardingDocumentFilesInputs,
  getOnboardingModuleDocumentInputs,
} from './documentsSection';

export type EditJobsiteConfigTabFormData = Pick<
  EditJobsiteConfigurationFormData,
  'documents'
>;

export const getFormInputs =
  (): TypedFormInputs<EditJobsiteConfigTabFormData> => {
    return {
      documents: {
        element: FormInputTypes.Section,
        layout: '',
        elementProps: {
          title: 'Documents',
          details: 'Indicate which fields are required',
        },
        children: documentsSectionInputs,
      },
    };
  };

type GetDefaultValuesArgs = {
  jobsite: Jobsite;
};

export const getDefaultValues = (
  args: GetDefaultValuesArgs,
): EditJobsiteConfigTabFormData => {
  const { jobsite } = args;
  const documents = getDocumentsSectionDefaultValues(jobsite);
  return { documents };
};

const getOnboardingModuleInput = (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): JobsiteUpdateInput['onboardingModule'] => {
  const { documents } = getOnboardingModuleDocumentInputs(
    data.documents,
    dirtyFields.documents,
  );

  const hasChanged = Boolean(documents?.length);

  return hasChanged
    ? {
        name: `${jobsite.name} - onboarding`,
        slug: `${titleToKebabCase(jobsite.name)}-onboarding`,
        isEnabled: true,
        documents,
      }
    : undefined;
};

export const getJobsiteUpdateInput = async (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): Promise<JobsiteUpdateInput> => {
  return ensureNonUndefinedFields<JobsiteUpdateInput>({
    jobsiteId: jobsite.jobsiteId,
    onboardingDocumentFiles: await getOnboardingDocumentFilesInputs(
      jobsite,
      data.documents,
      dirtyFields.documents,
    ),
    onboardingModule: getOnboardingModuleInput(jobsite, data, dirtyFields),
  });
};
